import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import ErrorIcon from '../../assets/error.svg';
import ExitIcon from '../../assets/exit.svg';
import ConfirmIcon from '../../assets/check.svg';
import cancelIcon from '../../assets/cancel.svg';
import VoiceJourneyFooter from './VoiceJourneyFooter';

const VoiceJourneyRedirectionCard = ({ statusCode, customDetails }) => {
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));

	// Status configuration map
	const statusConfig = {
		200: {
			icon: ConfirmIcon,
			iconSize: '100px',
			title: 'Consent Approved by Customer',
			subtitle: `${global.entityName} will now be able to access the bank statements shared`,
		},
		201: {
			icon: cancelIcon,
			iconSize: '100px',
			title: 'Consent Rejected by Customer',
			subtitle: `${global.entityName} cannot access their bank statements. You can try reinitiating a new call to the customer`,
		},
		411: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Failed to initiate the call',
		},
		412: {
			icon: ExitIcon,
			iconSize: '100px',
			title: 'Customer ended the call',
			subtitle:
				'The conversation ended early. You may try calling again if needed.',
		},
		413: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Automated call ended',
			subtitle:
				'Start another call if you need to reconnect with the customer.',
		},
		414: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Customer did not answer the call',
			subtitle: 'Try placing the call again or reach out at a different time.',
		},
		415: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Maximum call duration reached',
			subtitle:
				'If the conversation needs to continue, you can start another call.',
		},
		416: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call went to voicemail',
			subtitle: 'The customer may be unavailable. Try calling again later.',
		},
		417: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'No response from the customer',
			subtitle:
				'The customer did not respond during the call. You can try placing the call again.',
		},
		418: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
		},
		419: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Customer declined the call',
			subtitle: 'The customer may be busy. You can try again later.',
		},
		420: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Customer declined the call',
			subtitle: 'The customer may be busy. You can try again later.',
		},
		421: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be connected',
			subtitle:
				'We were unable to connect the call to the customer. Please try again later.',
		},
		422: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call disconnected',
			subtitle:
				'The call was disconnected due to network issues. You can try placing the call again.',
		},
		423: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Something Went Wrong',
			subtitle: 'An error occurred. Please try again',
		},
	};

	const config = statusConfig[statusCode] || statusConfig.default;

	const getStatusIcon = () => config.icon;
	const getTitle = () => config.title;
	const getSubtitle = () => config.subtitle;
	const getIconSize = () => config.iconSize;

	const contentBox = (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				textAlign: 'center',
				gap: '8px',
			}}
		>
			<Box
				component="img"
				src={getStatusIcon()}
				alt="status"
				sx={{
					width: getIconSize(),
					height: getIconSize(),
				}}
			/>
			<Typography
				sx={{
					fontSize: '16px',
					fontWeight: 700,
					color: theme.palette.text.primary,
					fontFamily: customDetails?.fontFamily,
				}}
			>
				{getTitle()}
			</Typography>
			<Typography
				sx={{
					fontSize: '14px',
					color: theme.palette.text.secondary,
					fontFamily: customDetails?.fontFamily,
				}}
			>
				{getSubtitle()}
			</Typography>
		</Box>
	);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				minHeight: '100vh',
				paddingTop: mobileScreen ? '80px' : '96px',
				backgroundColor: theme.palette.primary.light,
			}}
		>
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: mobileScreen ? '2rem 1.5rem' : '2rem',
				}}
			>
				{mobileScreen ? (
					contentBox
				) : (
					<Box
						sx={{
							padding: '3rem 4rem',
							maxWidth: '600px',
							minWidth: '500px',
							border: `1px solid ${theme.palette.divider}`,
							minHeight: '300px',
							borderRadius: '8px',
							backgroundColor: theme.palette.background.paper,
							boxShadow: 'none',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						{contentBox}
					</Box>
				)}
			</Box>
			<VoiceJourneyFooter customDetails={customDetails} />
		</Box>
	);
};

export default VoiceJourneyRedirectionCard;
